import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Switch } from '../../components/ui/switch';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '../../components/ui/collapsible';
import { toast } from 'sonner';
import {
  Save,
  Loader2,
  ArrowLeft,
  Upload,
  Eye,
  Plus,
  X,
  Trash2,
  ChevronDown,
  ChevronUp,
  Shield,
  TrendingUp,
  BarChart3,
  Award,
  Users,
  CheckCircle,
  Image,
  Phone,
  HelpCircle,
  Layers,
  Download,
  Star,
  Clock,
  AlertCircle,
  ArrowUpDown,
  FileWarning,
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const ICON_OPTIONS = [
  { value: 'shield', label: 'Shield', icon: Shield },
  { value: 'trending', label: 'Trending', icon: TrendingUp },
  { value: 'chart', label: 'Chart', icon: BarChart3 },
  { value: 'award', label: 'Award', icon: Award },
  { value: 'users', label: 'Users', icon: Users },
  { value: 'check', label: 'Check', icon: CheckCircle },
];

const CATEGORY_OPTIONS = ['Kids', 'Schools', 'Adults', 'Holiday'];

const MAX_FEATURED = 3;
const MAX_IMAGE_SIZE_MB = 2;
const MAX_PDF_SIZE_MB = 10;

const ProgramsPathwaysEditor = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [isDirty, setIsDirty] = useState(false);
  const isDirtyRef = useRef(false);
  const [uploading, setUploading] = useState({});
  
  // Store selected files per program (by ID)
  const [programImageFiles, setProgramImageFiles] = useState({});
  const [programFlyerFiles, setProgramFlyerFiles] = useState({});
  
  const [expandedSections, setExpandedSections] = useState({
    hero: true,
    pathways: true,
    ladder: false,
    faq: false,
    cta: false,
  });

  const [config, setConfig] = useState({
    hero_title: '',
    hero_subtitle: '',
    trust_chips: [],
    pathways: [],
    ladder_title: '',
    ladder_subtitle: '',
    ladder_steps: [],
    faq_title: '',
    faq_subtitle: '',
    faqs: [],
    cta_title: '',
    cta_subtitle: '',
    cta_phone: '',
    cta_whatsapp: '',
    cta_email: '',
    cta_primary_text: '',
    cta_primary_link: '',
    cta_secondary_text: '',
    homepage_title: '',
    homepage_subtitle: '',
    homepage_cta_text: '',
    homepage_featured_count: 3,
    updated_at: null,
  });

  const updateConfig = useCallback((updater) => {
    setConfig(prev => typeof updater === 'function' ? updater(prev) : updater);
    setIsDirty(true);
    isDirtyRef.current = true;
  }, []);

  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (isDirtyRef.current) {
        e.preventDefault();
        e.returnValue = '';
      }
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, []);

  const handleNavigateBack = () => {
    if (isDirty && !window.confirm('You have unsaved changes. Leave anyway?')) return;
    navigate(-1);
  };

  useEffect(() => { fetchConfig(); }, []);

  const fetchConfig = async () => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/landing/programs-pathways-config`);
      if (res.ok) {
        const data = await res.json();
        // Migrate legacy "visible" field to "status"
        if (data.pathways) {
          data.pathways = data.pathways.map(p => ({
            ...p,
            status: p.status || (p.visible === false ? 'draft' : 'published'),
          }));
        }
        setConfig(data);
        setIsDirty(false);
        isDirtyRef.current = false;
      }
    } catch (error) {
      toast.error('Failed to load config');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/landing/programs-pathways-config`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
        body: JSON.stringify(config),
      });
      if (res.ok) {
        const result = await res.json();
        toast.success('Programs saved successfully');
        setIsDirty(false);
        isDirtyRef.current = false;
        fetchConfig();
      } else throw new Error();
    } catch {
      toast.error('Failed to save');
    } finally {
      setSaving(false);
    }
  };

  // File validation
  const validateImageFile = (file) => {
    // Normalize image/jpg to image/jpeg
    const fileType = file.type === 'image/jpg' ? 'image/jpeg' : file.type;
    const allowedTypes = ['image/jpeg', 'image/png', 'image/webp'];
    if (!allowedTypes.includes(fileType)) {
      toast.error('Only JPG, JPEG, PNG, and WebP images are allowed');
      return false;
    }
    if (file.size > MAX_IMAGE_SIZE_MB * 1024 * 1024) {
      toast.warning(`Image is large (${(file.size / 1024 / 1024).toFixed(1)}MB). Recommend under ${MAX_IMAGE_SIZE_MB}MB for faster loading.`);
    }
    return true;
  };

  const validatePdfFile = (file) => {
    // Flyers can be PDF or images
    const fileType = file.type === 'image/jpg' ? 'image/jpeg' : file.type;
    const allowedTypes = ['application/pdf', 'image/jpeg', 'image/png', 'image/webp'];
    if (!allowedTypes.includes(fileType)) {
      toast.error('Only PDF, JPG, JPEG, PNG, and WebP files are allowed');
      return false;
    }
    if (file.size > MAX_PDF_SIZE_MB * 1024 * 1024) {
      toast.warning(`File is large (${(file.size / 1024 / 1024).toFixed(1)}MB). Recommend under ${MAX_PDF_SIZE_MB}MB.`);
    }
    return true;
  };

  const handleFileUpload = async (file, fileType, pathwayId = null) => {
    if (!file) {
      toast.error('Please choose a file first');
      return null;
    }
    
    const uploadKey = pathwayId ? `${fileType}_${pathwayId}` : fileType;
    
    // Prevent duplicate uploads
    if (uploading[uploadKey]) {
      toast.warning('Upload already in progress');
      return null;
    }
    
    setUploading(prev => ({ ...prev, [uploadKey]: true }));
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('image_type', fileType);
      if (pathwayId) {
        formData.append('item_id', pathwayId);
      }
      
      const res = await fetch(`${BACKEND_URL}/api/landing/upload`, {
        method: 'POST',
        headers: { 
          Authorization: `Bearer ${localStorage.getItem('token')}`
          // Do NOT set Content-Type - let browser set multipart/form-data with boundary
        },
        body: formData,
      });
      
      if (res.ok) {
        const data = await res.json();
        toast.success('File uploaded successfully');
        return data.image_url || data.url;
      } else {
        // Show backend error message
        const errorData = await res.json().catch(() => ({}));
        const errorMsg = errorData.detail || `Upload failed (${res.status})`;
        toast.error(errorMsg);
        return null;
      }
    } catch (err) {
      console.error('Upload error:', err);
      toast.error('Upload failed: Network error');
      return null;
    } finally {
      setUploading(prev => ({ ...prev, [uploadKey]: false }));
    }
  };

  const getFileUrl = (url) => {
    if (!url) return null;
    if (url.startsWith('http')) return url;
    if (typeof window !== 'undefined') {
      return `${window.location.origin}${url}`;
    }
    return `${BACKEND_URL}${url}`;
  };

  const toggleSection = (key) => {
    setExpandedSections(prev => ({ ...prev, [key]: !prev[key] }));
  };

  // Trust chips handlers
  const updateChip = (idx, field, value) => {
    updateConfig(prev => ({
      ...prev,
      trust_chips: prev.trust_chips.map((c, i) => i === idx ? { ...c, [field]: value } : c),
    }));
  };

  // Pathway handlers
  const addPathway = () => {
    const newPathway = {
      id: `pathway-${Date.now()}`,
      title: 'New Program',
      tagline: '',
      best_for: '',
      image_url: null,
      bullets: [''],
      outcome_badge: '',
      duration_text: '',
      primary_cta_text: 'Enquire Now',
      primary_cta_link: '/#contact',
      flyer_pdf_url: null,
      flyer_label: 'Download Flyer',
      status: 'draft', // New programs start as draft
      featured: false,
      order: config.pathways.length + 1,
      categories: [], // Empty categories by default
    };
    updateConfig(prev => ({ ...prev, pathways: [...prev.pathways, newPathway] }));
  };

  const updatePathway = (id, field, value) => {
    updateConfig(prev => ({
      ...prev,
      pathways: prev.pathways.map(p => p.id === id ? { ...p, [field]: value } : p),
    }));
  };

  const removePathway = (id) => {
    if (!window.confirm('Delete this pathway?')) return;
    updateConfig(prev => ({ ...prev, pathways: prev.pathways.filter(p => p.id !== id) }));
  };

  // Featured toggle with max 3 enforcement
  const toggleFeatured = (id, newValue) => {
    const currentFeaturedCount = config.pathways.filter(p => p.featured && p.id !== id).length;
    
    if (newValue && currentFeaturedCount >= MAX_FEATURED) {
      // Auto-unfeature oldest featured program
      const sortedFeatured = config.pathways
        .filter(p => p.featured && p.id !== id)
        .sort((a, b) => (a.order || 0) - (b.order || 0));
      
      const oldestFeatured = sortedFeatured[0];
      if (oldestFeatured) {
        toast.info(`Max ${MAX_FEATURED} featured. Unfeatured "${oldestFeatured.title}"`);
        updateConfig(prev => ({
          ...prev,
          pathways: prev.pathways.map(p => {
            if (p.id === oldestFeatured.id) return { ...p, featured: false };
            if (p.id === id) return { ...p, featured: true };
            return p;
          }),
        }));
        return;
      }
    }
    
    updatePathway(id, 'featured', newValue);
  };

  // Move pathway up/down
  const movePathway = (id, direction) => {
    const sortedPathways = [...config.pathways].sort((a, b) => (a.order || 0) - (b.order || 0));
    const currentIdx = sortedPathways.findIndex(p => p.id === id);
    
    if (direction === 'up' && currentIdx > 0) {
      const targetIdx = currentIdx - 1;
      const currentOrder = sortedPathways[currentIdx].order || currentIdx + 1;
      const targetOrder = sortedPathways[targetIdx].order || targetIdx + 1;
      
      updateConfig(prev => ({
        ...prev,
        pathways: prev.pathways.map(p => {
          if (p.id === sortedPathways[currentIdx].id) return { ...p, order: targetOrder };
          if (p.id === sortedPathways[targetIdx].id) return { ...p, order: currentOrder };
          return p;
        }),
      }));
    } else if (direction === 'down' && currentIdx < sortedPathways.length - 1) {
      const targetIdx = currentIdx + 1;
      const currentOrder = sortedPathways[currentIdx].order || currentIdx + 1;
      const targetOrder = sortedPathways[targetIdx].order || targetIdx + 1;
      
      updateConfig(prev => ({
        ...prev,
        pathways: prev.pathways.map(p => {
          if (p.id === sortedPathways[currentIdx].id) return { ...p, order: targetOrder };
          if (p.id === sortedPathways[targetIdx].id) return { ...p, order: currentOrder };
          return p;
        }),
      }));
    }
  };

  // Category toggle handler
  const toggleCategory = (pathwayId, category) => {
    updateConfig(prev => ({
      ...prev,
      pathways: prev.pathways.map(p => {
        if (p.id !== pathwayId) return p;
        const categories = p.categories || [];
        if (categories.includes(category)) {
          return { ...p, categories: categories.filter(c => c !== category) };
        } else {
          return { ...p, categories: [...categories, category] };
        }
      }),
    }));
  };

  const updatePathwayBullet = (pathwayId, bulletIdx, value) => {
    updateConfig(prev => ({
      ...prev,
      pathways: prev.pathways.map(p => {
        if (p.id !== pathwayId) return p;
        const bullets = [...(p.bullets || [])];
        bullets[bulletIdx] = value;
        return { ...p, bullets };
      }),
    }));
  };

  const addPathwayBullet = (pathwayId) => {
    updateConfig(prev => ({
      ...prev,
      pathways: prev.pathways.map(p =>
        p.id === pathwayId && (p.bullets?.length || 0) < 4
          ? { ...p, bullets: [...(p.bullets || []), ''] }
          : p
      ),
    }));
  };

  const removePathwayBullet = (pathwayId, bulletIdx) => {
    updateConfig(prev => ({
      ...prev,
      pathways: prev.pathways.map(p => {
        if (p.id !== pathwayId) return p;
        return { ...p, bullets: p.bullets.filter((_, i) => i !== bulletIdx) };
      }),
    }));
  };

  // Store file in state and immediately upload
  const handleImageFileSelect = (e, pathwayId) => {
    const file = e.target.files?.[0];
    e.target.value = ''; // Reset for re-upload same file
    
    if (!file) {
      toast.error('Please select a file first');
      return;
    }
    
    // Store file in state
    setProgramImageFiles(prev => ({ ...prev, [pathwayId]: file }));
    
    // Immediately upload
    uploadProgramImageDirect(file, pathwayId);
  };
  
  const handleFlyerFileSelect = (e, pathwayId) => {
    const file = e.target.files?.[0];
    e.target.value = ''; // Reset for re-upload same file
    
    if (!file) {
      toast.error('Please select a file first');
      return;
    }
    
    // Store file in state
    setProgramFlyerFiles(prev => ({ ...prev, [pathwayId]: file }));
    
    // Immediately upload
    uploadProgramFlyerDirect(file, pathwayId);
  };

  // Direct upload handler for program images
  const uploadProgramImageDirect = async (file, pathwayId) => {
    if (!file) {
      toast.error('No file selected. Please choose a file first.');
      return;
    }
    
    // Validate file type
    const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
    if (!validTypes.includes(file.type)) {
      toast.error('Only JPG, PNG, and WebP images are allowed');
      return;
    }
    
    const uploadKey = `program_card_image_${pathwayId}`;
    if (uploading[uploadKey]) {
      toast.warning('Upload already in progress');
      return;
    }
    
    setUploading(prev => ({ ...prev, [uploadKey]: true }));
    
    try {
      // Build FormData with explicit file and image_type
      const formData = new FormData();
      formData.append('file', file);
      formData.append('image_type', 'program_card_image');
      formData.append('item_id', pathwayId);
      
      // Debug log
      console.log('Uploading image:', { pathwayId, fileName: file.name, fileType: file.type, fileSize: file.size });
      
      const response = await fetch(`${BACKEND_URL}/api/landing/upload`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
          // Do NOT set Content-Type - browser will set multipart/form-data with boundary
        },
        body: formData
      });
      
      if (response.ok) {
        const data = await response.json();
        const imageUrl = data.image_url || data.url;
        updatePathway(pathwayId, 'image_url', imageUrl);
        // Clear stored file after successful upload
        setProgramImageFiles(prev => ({ ...prev, [pathwayId]: null }));
        toast.success('Image uploaded successfully');
      } else {
        const errorData = await response.json().catch(() => ({ detail: 'Upload failed' }));
        console.error('Upload error response:', errorData);
        toast.error(errorData.detail || `Upload failed (${response.status})`);
      }
    } catch (error) {
      console.error('Upload error:', error);
      toast.error('Upload failed: Network error');
    } finally {
      setUploading(prev => ({ ...prev, [uploadKey]: false }));
    }
  };

  // Direct upload handler for program flyers
  const uploadProgramFlyerDirect = async (file, pathwayId) => {
    if (!file) {
      toast.error('No file selected. Please choose a file first.');
      return;
    }
    
    // Validate file type
    const validTypes = ['application/pdf', 'image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
    if (!validTypes.includes(file.type)) {
      toast.error('Only PDF, JPG, PNG, and WebP files are allowed');
      return;
    }
    
    const uploadKey = `program_flyer_${pathwayId}`;
    if (uploading[uploadKey]) {
      toast.warning('Upload already in progress');
      return;
    }
    
    setUploading(prev => ({ ...prev, [uploadKey]: true }));
    
    try {
      // Build FormData with explicit file and image_type
      const formData = new FormData();
      formData.append('file', file);
      formData.append('image_type', 'program_flyer');
      formData.append('item_id', pathwayId);
      
      // Debug log
      console.log('Uploading flyer:', { pathwayId, fileName: file.name, fileType: file.type, fileSize: file.size });
      
      const response = await fetch(`${BACKEND_URL}/api/landing/upload`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: formData
      });
      
      if (response.ok) {
        const data = await response.json();
        const fileUrl = data.image_url || data.url;
        updatePathway(pathwayId, 'flyer_pdf_url', fileUrl);
        // Clear stored file after successful upload
        setProgramFlyerFiles(prev => ({ ...prev, [pathwayId]: null }));
        toast.success('Flyer uploaded successfully');
      } else {
        const errorData = await response.json().catch(() => ({ detail: 'Upload failed' }));
        console.error('Upload error response:', errorData);
        toast.error(errorData.detail || `Upload failed (${response.status})`);
      }
    } catch (error) {
      console.error('Upload error:', error);
      toast.error('Upload failed: Network error');
    } finally {
      setUploading(prev => ({ ...prev, [uploadKey]: false }));
    }
  };

  // Ladder handlers
  const addLadderStep = () => {
    if (config.ladder_steps.length >= 6) return toast.error('Maximum 6 steps');
    updateConfig(prev => ({
      ...prev,
      ladder_steps: [...prev.ladder_steps, { id: `step-${Date.now()}`, label: '', description: '' }],
    }));
  };

  const updateLadderStep = (id, field, value) => {
    updateConfig(prev => ({
      ...prev,
      ladder_steps: prev.ladder_steps.map(s => s.id === id ? { ...s, [field]: value } : s),
    }));
  };

  const removeLadderStep = (id) => {
    updateConfig(prev => ({ ...prev, ladder_steps: prev.ladder_steps.filter(s => s.id !== id) }));
  };

  // FAQ handlers
  const addFaq = () => {
    if (config.faqs.length >= 8) return toast.error('Maximum 8 FAQs');
    updateConfig(prev => ({
      ...prev,
      faqs: [...prev.faqs, { id: `faq-${Date.now()}`, question: '', answer: '' }],
    }));
  };

  const updateFaq = (id, field, value) => {
    updateConfig(prev => ({
      ...prev,
      faqs: prev.faqs.map(f => f.id === id ? { ...f, [field]: value } : f),
    }));
  };

  const removeFaq = (id) => {
    updateConfig(prev => ({ ...prev, faqs: prev.faqs.filter(f => f.id !== id) }));
  };

  // Stats
  const publishedCount = config.pathways?.filter(p => p.status === 'published').length || 0;
  const draftCount = config.pathways?.filter(p => p.status === 'draft').length || 0;
  const featuredCount = config.pathways?.filter(p => p.featured).length || 0;
  const sortedPathways = [...(config.pathways || [])].sort((a, b) => (a.order || 0) - (b.order || 0));

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in max-w-5xl mx-auto p-6" data-testid="programs-editor">
      {/* Header */}
      <div className="flex items-center justify-between sticky top-0 bg-slate-50 py-4 -mx-6 px-6 z-10 border-b">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="sm" onClick={handleNavigateBack}>
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back
          </Button>
          <div>
            <h1 className="text-2xl font-heading font-bold text-slate-900">Programs & Pathways</h1>
            <div className="flex items-center gap-3 text-sm text-slate-500">
              <span>Manage swim programs and progression pathways</span>
              {config.updated_at && (
                <span className="flex items-center gap-1 text-xs text-slate-400">
                  <Clock className="w-3 h-3" />
                  Updated {new Date(config.updated_at).toLocaleDateString()} at {new Date(config.updated_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                </span>
              )}
            </div>
          </div>
        </div>
        <div className="flex gap-3">
          <Button variant="outline" onClick={() => window.open('/programs', '_blank')}>
            <Eye className="w-4 h-4 mr-2" />
            Preview
          </Button>
          <Button onClick={handleSave} disabled={saving} className="bg-primary" data-testid="save-programs-btn">
            {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
            Save
          </Button>
        </div>
      </div>

      {/* Hero Section */}
      <Collapsible open={expandedSections.hero} onOpenChange={() => toggleSection('hero')}>
        <Card>
          <CollapsibleTrigger asChild>
            <CardHeader className="cursor-pointer hover:bg-slate-50">
              <CardTitle className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Layers className="w-5 h-5 text-primary" />
                  Page Hero & Homepage Settings
                </div>
                {expandedSections.hero ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
              </CardTitle>
            </CardHeader>
          </CollapsibleTrigger>
          <CollapsibleContent>
            <CardContent className="space-y-6 pt-0">
              {/* Full page hero */}
              <div className="space-y-4">
                <h4 className="font-medium text-slate-700">Full Page (/programs)</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label>Title</Label>
                    <Input value={config.hero_title} onChange={e => updateConfig(p => ({ ...p, hero_title: e.target.value }))} />
                  </div>
                  <div>
                    <Label>Subtitle</Label>
                    <Input value={config.hero_subtitle} onChange={e => updateConfig(p => ({ ...p, hero_subtitle: e.target.value }))} />
                  </div>
                </div>
                
                {/* Trust Chips */}
                <div>
                  <Label className="mb-2 block">Trust Chips (4)</Label>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    {config.trust_chips?.map((chip, idx) => (
                      <div key={idx} className="p-3 bg-slate-50 rounded-lg space-y-2">
                        <Input
                          value={chip.label}
                          onChange={e => updateChip(idx, 'label', e.target.value)}
                          placeholder="Label"
                          className="text-sm"
                        />
                        <Select value={chip.icon} onValueChange={v => updateChip(idx, 'icon', v)}>
                          <SelectTrigger className="text-sm"><SelectValue /></SelectTrigger>
                          <SelectContent>
                            {ICON_OPTIONS.map(opt => (
                              <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              <hr />

              {/* Homepage section settings */}
              <div className="space-y-4">
                <h4 className="font-medium text-slate-700">Homepage Section</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label>Title</Label>
                    <Input value={config.homepage_title} onChange={e => updateConfig(p => ({ ...p, homepage_title: e.target.value }))} />
                  </div>
                  <div>
                    <Label>Subtitle</Label>
                    <Input value={config.homepage_subtitle} onChange={e => updateConfig(p => ({ ...p, homepage_subtitle: e.target.value }))} />
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label>CTA Button Text</Label>
                    <Input value={config.homepage_cta_text} onChange={e => updateConfig(p => ({ ...p, homepage_cta_text: e.target.value }))} />
                  </div>
                  <div>
                    <Label>Featured Programs Count (max 3)</Label>
                    <Select value={String(config.homepage_featured_count)} onValueChange={v => updateConfig(p => ({ ...p, homepage_featured_count: parseInt(v) }))}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="1">1</SelectItem>
                        <SelectItem value="2">2</SelectItem>
                        <SelectItem value="3">3</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </div>
            </CardContent>
          </CollapsibleContent>
        </Card>
      </Collapsible>

      {/* Pathways Manager */}
      <Collapsible open={expandedSections.pathways} onOpenChange={() => toggleSection('pathways')}>
        <Card>
          <CollapsibleTrigger asChild>
            <CardHeader className="cursor-pointer hover:bg-slate-50">
              <CardTitle className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Users className="w-5 h-5 text-primary" />
                  Pathways / Programs
                  <div className="flex items-center gap-2 ml-2">
                    <Badge variant="secondary" className="text-xs">{publishedCount} published</Badge>
                    {draftCount > 0 && <Badge variant="outline" className="text-xs text-amber-600 border-amber-300">{draftCount} draft</Badge>}
                    <Badge variant="outline" className={`text-xs ${featuredCount >= MAX_FEATURED ? 'text-amber-600 border-amber-300' : ''}`}>
                      <Star className="w-3 h-3 mr-1" />
                      {featuredCount}/{MAX_FEATURED} featured
                    </Badge>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Button variant="outline" size="sm" onClick={(e) => { e.stopPropagation(); addPathway(); }}>
                    <Plus className="w-4 h-4 mr-1" />
                    Add
                  </Button>
                  {expandedSections.pathways ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
                </div>
              </CardTitle>
            </CardHeader>
          </CollapsibleTrigger>
          <CollapsibleContent>
            <CardContent className="space-y-4 pt-0">
              {sortedPathways.map((pathway, pIdx) => (
                <div 
                  key={pathway.id} 
                  className={`p-4 border rounded-lg space-y-4 relative ${
                    pathway.status === 'draft' ? 'bg-amber-50/50 border-amber-200' : 'bg-white'
                  }`}
                >
                  {/* Top controls bar */}
                  <div className="flex items-center justify-between pb-2 border-b">
                    <div className="flex items-center gap-3">
                      {/* Order controls */}
                      <div className="flex flex-col">
                        <button
                          onClick={() => movePathway(pathway.id, 'up')}
                          disabled={pIdx === 0}
                          className="p-0.5 hover:bg-slate-100 rounded disabled:opacity-30 disabled:cursor-not-allowed"
                          title="Move up"
                        >
                          <ChevronUp className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => movePathway(pathway.id, 'down')}
                          disabled={pIdx === sortedPathways.length - 1}
                          className="p-0.5 hover:bg-slate-100 rounded disabled:opacity-30 disabled:cursor-not-allowed"
                          title="Move down"
                        >
                          <ChevronDown className="w-4 h-4" />
                        </button>
                      </div>
                      <span className="text-xs text-slate-400 w-6">#{pathway.order || pIdx + 1}</span>
                      
                      {/* Status badge */}
                      {pathway.status === 'draft' ? (
                        <Badge variant="outline" className="bg-amber-100 text-amber-700 border-amber-300">Draft</Badge>
                      ) : (
                        <Badge variant="outline" className="bg-emerald-100 text-emerald-700 border-emerald-300">Published</Badge>
                      )}
                      
                      {/* Featured badge */}
                      {pathway.featured && (
                        <Badge className="bg-primary/10 text-primary border-primary/20">
                          <Star className="w-3 h-3 mr-1 fill-current" />
                          Featured
                        </Badge>
                      )}
                    </div>
                    
                    <div className="flex items-center gap-4">
                      {/* Status toggle */}
                      <div className="flex items-center gap-2">
                        <Switch
                          checked={pathway.status === 'published'}
                          onCheckedChange={v => updatePathway(pathway.id, 'status', v ? 'published' : 'draft')}
                        />
                        <Label className="text-xs cursor-pointer">Published</Label>
                      </div>
                      
                      {/* Featured toggle */}
                      <div className="flex items-center gap-2">
                        <Switch
                          checked={pathway.featured}
                          onCheckedChange={v => toggleFeatured(pathway.id, v)}
                        />
                        <Label className="text-xs cursor-pointer flex items-center gap-1">
                          <Star className="w-3 h-3" />
                          Featured
                        </Label>
                      </div>
                      
                      {/* Delete */}
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => removePathway(pathway.id)}
                        className="text-red-500 hover:text-red-700 hover:bg-red-50"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>

                  {/* Row 1: Image + Title/Tagline */}
                  <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                    {/* Image */}
                    <div>
                      <Label className="text-xs mb-1 block">
                        Image
                        <span className="text-slate-400 font-normal ml-1">(JPG/JPEG/PNG/WebP)</span>
                      </Label>
                      <input
                        type="file"
                        accept=".jpg,.jpeg,.png,.webp,image/jpeg,image/png,image/webp"
                        className="hidden"
                        id={`img-${pathway.id}`}
                        onChange={(e) => handleImageFileSelect(e, pathway.id)}
                        disabled={uploading[`program_card_image_${pathway.id}`]}
                      />
                      <label
                        htmlFor={`img-${pathway.id}`}
                        className={`block aspect-video rounded-lg border-2 border-dashed border-slate-300 hover:border-primary cursor-pointer overflow-hidden bg-slate-50 ${uploading[`program_card_image_${pathway.id}`] ? 'opacity-50 cursor-wait' : ''}`}
                      >
                        {uploading[`program_card_image_${pathway.id}`] ? (
                          <div className="h-full flex items-center justify-center">
                            <Loader2 className="w-6 h-6 animate-spin" />
                          </div>
                        ) : pathway.image_url ? (
                          <div className="relative h-full group">
                            <img src={getFileUrl(pathway.image_url)} alt="" className="w-full h-full object-cover" />
                            <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                              <span className="text-white text-xs">Click to replace</span>
                            </div>
                          </div>
                        ) : (
                          <div className="h-full flex flex-col items-center justify-center text-slate-400">
                            <Image className="w-8 h-8 mb-1" />
                            <span className="text-xs">Upload (1200×800)</span>
                          </div>
                        )}
                      </label>
                      {pathway.image_url && (
                        <button
                          type="button"
                          onClick={() => updatePathway(pathway.id, 'image_url', null)}
                          className="text-xs text-red-500 hover:text-red-700 mt-1 flex items-center gap-1"
                        >
                          <X className="w-3 h-3" />
                          Remove Image
                        </button>
                      )}
                      {!pathway.image_url && (
                        <p className="text-xs text-amber-600 mt-1 flex items-center gap-1">
                          <FileWarning className="w-3 h-3" />
                          Re-upload recommended
                        </p>
                      )}
                    </div>

                    {/* Title, Tagline, Best For */}
                    <div className="md:col-span-3 space-y-3">
                      <div>
                        <Label className="text-xs">Title</Label>
                        <Input value={pathway.title} onChange={e => updatePathway(pathway.id, 'title', e.target.value)} />
                      </div>
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <Label className="text-xs">Best For (age/level)</Label>
                          <Input value={pathway.best_for} onChange={e => updatePathway(pathway.id, 'best_for', e.target.value)} placeholder="Ages 6-12 • Beginners" />
                        </div>
                        <div>
                          <Label className="text-xs">Tagline</Label>
                          <Input value={pathway.tagline} onChange={e => updatePathway(pathway.id, 'tagline', e.target.value)} />
                        </div>
                      </div>
                      {/* Categories */}
                      <div>
                        <Label className="text-xs mb-2 block">Categories (for filtering)</Label>
                        <div className="flex flex-wrap gap-2">
                          {CATEGORY_OPTIONS.map(cat => {
                            const isSelected = (pathway.categories || []).includes(cat);
                            return (
                              <button
                                key={cat}
                                type="button"
                                onClick={() => toggleCategory(pathway.id, cat)}
                                className={`px-3 py-1.5 text-xs rounded-full border transition-colors ${
                                  isSelected
                                    ? 'bg-primary text-white border-primary'
                                    : 'bg-white text-slate-600 border-slate-200 hover:border-primary hover:text-primary'
                                }`}
                              >
                                {cat}
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Row 2: Bullets */}
                  <div>
                    <Label className="text-xs mb-2 block">What You'll Learn (max 4)</Label>
                    <div className="space-y-2">
                      {pathway.bullets?.map((bullet, bIdx) => (
                        <div key={bIdx} className="flex gap-2">
                          <Input value={bullet} onChange={e => updatePathwayBullet(pathway.id, bIdx, e.target.value)} placeholder={`Skill ${bIdx + 1}`} />
                          <Button variant="ghost" size="icon" onClick={() => removePathwayBullet(pathway.id, bIdx)} className="text-red-500">
                            <X className="w-4 h-4" />
                          </Button>
                        </div>
                      ))}
                      {(pathway.bullets?.length || 0) < 4 && (
                        <Button variant="outline" size="sm" onClick={() => addPathwayBullet(pathway.id)}>
                          <Plus className="w-4 h-4 mr-1" />
                          Add Bullet
                        </Button>
                      )}
                    </div>
                  </div>

                  {/* Row 3: Badge, Duration, CTA */}
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    <div>
                      <Label className="text-xs">Outcome Badge</Label>
                      <Input value={pathway.outcome_badge} onChange={e => updatePathway(pathway.id, 'outcome_badge', e.target.value)} placeholder="Water Confidence" />
                    </div>
                    <div>
                      <Label className="text-xs">Duration Text</Label>
                      <Input value={pathway.duration_text} onChange={e => updatePathway(pathway.id, 'duration_text', e.target.value)} placeholder="8 sessions" />
                    </div>
                    <div>
                      <Label className="text-xs">CTA Text</Label>
                      <Input value={pathway.primary_cta_text} onChange={e => updatePathway(pathway.id, 'primary_cta_text', e.target.value)} />
                    </div>
                    <div>
                      <Label className="text-xs">CTA Link</Label>
                      <Input value={pathway.primary_cta_link} onChange={e => updatePathway(pathway.id, 'primary_cta_link', e.target.value)} />
                    </div>
                  </div>

                  {/* Row 4: PDF */}
                  <div>
                    <Label className="text-xs mb-1 block">
                      Flyer (PDF or Image)
                      <span className="text-slate-400 font-normal ml-1">(PDF/JPG/PNG/WebP)</span>
                    </Label>
                    <div className="flex items-center gap-3">
                      <input
                        type="file"
                        accept=".pdf,.jpg,.jpeg,.png,.webp,application/pdf,image/jpeg,image/png,image/webp"
                        className="hidden"
                        id={`pdf-${pathway.id}`}
                        onChange={(e) => handleFlyerFileSelect(e, pathway.id)}
                        disabled={uploading[`program_flyer_${pathway.id}`]}
                      />
                      <label
                        htmlFor={`pdf-${pathway.id}`}
                        className={`flex items-center gap-2 px-3 py-2 border-2 border-dashed rounded-lg cursor-pointer hover:border-primary text-sm ${uploading[`program_flyer_${pathway.id}`] ? 'opacity-50 cursor-wait' : ''}`}
                      >
                        {uploading[`program_flyer_${pathway.id}`] ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          <>
                            <Upload className="w-4 h-4" />
                            {pathway.flyer_pdf_url ? 'Replace PDF' : 'Upload PDF'}
                          </>
                        )}
                      </label>
                      {pathway.flyer_pdf_url ? (
                        <>
                          <a
                            href={getFileUrl(pathway.flyer_pdf_url)}
                            target="_blank"
                            rel="noopener noreferrer"
                            onClick={e => e.stopPropagation()}
                            className="text-xs text-primary hover:underline flex items-center gap-1"
                          >
                            <Eye className="w-3 h-3" />
                            View
                          </a>
                          <a
                            href={getFileUrl(pathway.flyer_pdf_url)}
                            target="_blank"
                            rel="noopener noreferrer"
                            download
                            onClick={e => e.stopPropagation()}
                            className="text-xs text-slate-600 hover:underline flex items-center gap-1"
                          >
                            <Download className="w-3 h-3" />
                            Download
                          </a>
                          <button
                            type="button"
                            onClick={() => updatePathway(pathway.id, 'flyer_pdf_url', null)}
                            className="text-xs text-red-500 hover:text-red-700 flex items-center gap-1"
                          >
                            <X className="w-3 h-3" />
                            Remove
                          </button>
                        </>
                      ) : (
                        <span className="text-xs text-slate-400">No flyer uploaded</span>
                      )}
                    </div>
                  </div>
                </div>
              ))}

              {config.pathways?.length === 0 && (
                <div className="text-center py-8 text-slate-400">
                  <Users className="w-12 h-12 mx-auto mb-2 opacity-50" />
                  <p>No pathways yet. Click "Add" to create one.</p>
                </div>
              )}
            </CardContent>
          </CollapsibleContent>
        </Card>
      </Collapsible>

      {/* Progression Ladder */}
      <Collapsible open={expandedSections.ladder} onOpenChange={() => toggleSection('ladder')}>
        <Card>
          <CollapsibleTrigger asChild>
            <CardHeader className="cursor-pointer hover:bg-slate-50">
              <CardTitle className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <TrendingUp className="w-5 h-5 text-primary" />
                  Progression Ladder ({config.ladder_steps?.length || 0}/6)
                </div>
                <div className="flex items-center gap-2">
                  <Button variant="outline" size="sm" onClick={(e) => { e.stopPropagation(); addLadderStep(); }} disabled={config.ladder_steps?.length >= 6}>
                    <Plus className="w-4 h-4 mr-1" />
                    Add Step
                  </Button>
                  {expandedSections.ladder ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
                </div>
              </CardTitle>
            </CardHeader>
          </CollapsibleTrigger>
          <CollapsibleContent>
            <CardContent className="space-y-4 pt-0">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label>Section Title</Label>
                  <Input value={config.ladder_title} onChange={e => updateConfig(p => ({ ...p, ladder_title: e.target.value }))} />
                </div>
                <div>
                  <Label>Subtitle</Label>
                  <Input value={config.ladder_subtitle} onChange={e => updateConfig(p => ({ ...p, ladder_subtitle: e.target.value }))} />
                </div>
              </div>
              
              <div className="space-y-2">
                {config.ladder_steps?.map((step, idx) => (
                  <div key={step.id} className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg">
                    <div className="w-8 h-8 rounded-full bg-primary text-white flex items-center justify-center font-bold text-sm">
                      {idx + 1}
                    </div>
                    <Input
                      value={step.label}
                      onChange={e => updateLadderStep(step.id, 'label', e.target.value)}
                      placeholder="Step label"
                      className="flex-1"
                    />
                    <Input
                      value={step.description}
                      onChange={e => updateLadderStep(step.id, 'description', e.target.value)}
                      placeholder="Description"
                      className="flex-1"
                    />
                    <Button variant="ghost" size="icon" onClick={() => removeLadderStep(step.id)} className="text-red-500">
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                ))}
              </div>
            </CardContent>
          </CollapsibleContent>
        </Card>
      </Collapsible>

      {/* FAQ */}
      <Collapsible open={expandedSections.faq} onOpenChange={() => toggleSection('faq')}>
        <Card>
          <CollapsibleTrigger asChild>
            <CardHeader className="cursor-pointer hover:bg-slate-50">
              <CardTitle className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <HelpCircle className="w-5 h-5 text-primary" />
                  FAQ ({config.faqs?.length || 0}/8)
                </div>
                <div className="flex items-center gap-2">
                  <Button variant="outline" size="sm" onClick={(e) => { e.stopPropagation(); addFaq(); }} disabled={config.faqs?.length >= 8}>
                    <Plus className="w-4 h-4 mr-1" />
                    Add
                  </Button>
                  {expandedSections.faq ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
                </div>
              </CardTitle>
            </CardHeader>
          </CollapsibleTrigger>
          <CollapsibleContent>
            <CardContent className="space-y-4 pt-0">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label>Section Title</Label>
                  <Input value={config.faq_title} onChange={e => updateConfig(p => ({ ...p, faq_title: e.target.value }))} />
                </div>
                <div>
                  <Label>Subtitle</Label>
                  <Input value={config.faq_subtitle} onChange={e => updateConfig(p => ({ ...p, faq_subtitle: e.target.value }))} />
                </div>
              </div>
              
              {config.faqs?.map(faq => (
                <div key={faq.id} className="p-4 bg-slate-50 rounded-lg space-y-3 relative">
                  <Button variant="ghost" size="icon" onClick={() => removeFaq(faq.id)} className="absolute top-2 right-2 text-red-500">
                    <Trash2 className="w-4 h-4" />
                  </Button>
                  <div>
                    <Label className="text-xs">Question</Label>
                    <Input value={faq.question} onChange={e => updateFaq(faq.id, 'question', e.target.value)} />
                  </div>
                  <div>
                    <Label className="text-xs">Answer</Label>
                    <Textarea value={faq.answer} onChange={e => updateFaq(faq.id, 'answer', e.target.value)} rows={2} />
                  </div>
                </div>
              ))}
            </CardContent>
          </CollapsibleContent>
        </Card>
      </Collapsible>

      {/* CTA */}
      <Collapsible open={expandedSections.cta} onOpenChange={() => toggleSection('cta')}>
        <Card>
          <CollapsibleTrigger asChild>
            <CardHeader className="cursor-pointer hover:bg-slate-50">
              <CardTitle className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Phone className="w-5 h-5 text-primary" />
                  Call to Action
                </div>
                {expandedSections.cta ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
              </CardTitle>
            </CardHeader>
          </CollapsibleTrigger>
          <CollapsibleContent>
            <CardContent className="space-y-4 pt-0">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label>Title</Label>
                  <Input value={config.cta_title} onChange={e => updateConfig(p => ({ ...p, cta_title: e.target.value }))} />
                </div>
                <div>
                  <Label>Subtitle</Label>
                  <Input value={config.cta_subtitle} onChange={e => updateConfig(p => ({ ...p, cta_subtitle: e.target.value }))} />
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <Label>Phone</Label>
                  <Input value={config.cta_phone} onChange={e => updateConfig(p => ({ ...p, cta_phone: e.target.value }))} />
                </div>
                <div>
                  <Label>WhatsApp (no spaces)</Label>
                  <Input value={config.cta_whatsapp} onChange={e => updateConfig(p => ({ ...p, cta_whatsapp: e.target.value }))} />
                </div>
                <div>
                  <Label>Email</Label>
                  <Input value={config.cta_email} onChange={e => updateConfig(p => ({ ...p, cta_email: e.target.value }))} />
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <Label>Primary Button Text</Label>
                  <Input value={config.cta_primary_text} onChange={e => updateConfig(p => ({ ...p, cta_primary_text: e.target.value }))} />
                </div>
                <div>
                  <Label>Primary Button Link</Label>
                  <Input value={config.cta_primary_link} onChange={e => updateConfig(p => ({ ...p, cta_primary_link: e.target.value }))} />
                </div>
                <div>
                  <Label>Secondary Button Text</Label>
                  <Input value={config.cta_secondary_text} onChange={e => updateConfig(p => ({ ...p, cta_secondary_text: e.target.value }))} />
                </div>
              </div>
            </CardContent>
          </CollapsibleContent>
        </Card>
      </Collapsible>

      {/* Bottom Save */}
      <div className="flex justify-end pb-8">
        <Button onClick={handleSave} disabled={saving} size="lg" className="bg-primary">
          {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
          Save All Changes
        </Button>
      </div>
    </div>
  );
};

export default ProgramsPathwaysEditor;
